/* global Blockly */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Parser} from 'acorn';
import {simple} from 'acorn-walk';
import AppTour from '../AppTour';
import KeyBindings from './KeyBindings';
import ErrorMessage from '../ErrorMessage/ErrorMessage'
import queryString from 'query-string';
import classNames from 'classnames';
import DeleteBlockDialog from "../DeleteBlockDialog";
import "./index.css";
import ActionButtons from "../ActionButtons";
import Toolbox, { TOOLBOX_COLLAPSED_WIDTH } from '../Toolbox/Toolbox';

const DEFAULT_BLOCK_GARBAGE_CAN_FONT_SIZE = 32;
const DEFAULT_FLYOUT_WIDTH_INCREASE = 50;
const DEFAULT_GARBAGE_CAN_ICON_TOP_OFFSET = 7;
const DEFAULT_WARNING_SYMBOL_FONT_SIZE = 28;
const DEFAULT_WARNING_SYMBOL_SPACING = 14;
const DEFAULT_WARNING_SYMBOL_TOOLTIP_FONT_SIZE = 16;
const DEFAULT_WARNING_SYMBOL_TOOLTIP_WIDTH = 280;
const DELETE_BLOCK_ICON_CLASS_NAME = "delete-block-icon";
const DELETE_BLOCK_ICON_CONTAINER_CLASS_NAME = "delete-block-icon-container";
const GARBAGE_CAN_ICON_SPACING = 10;
const LIBRARY_TITLE_WARNING_ICON_CONTAINER_ID = "library-title-warning-icon-div";
const LIBRARY_TITLE_WARNING_ICON_ID = "library-title-warning-icon";
const LIBRARY_TITLE_WARNING_TOOLTIP = "Some blocks in this library have been hidden because they are incompatible with this app type.";
const LIBRARY_TITLE_WARNING_TOOLTIP_ID = "library-title-warning-tooltip";
const SCROLLBAR_HORIZONTAL_OFFSET = 15;
const SCROLLBAR_VERTICAL_OFFSET = 2.5;
const WARNING_ICON_CLASS = "snap-icon-warning_default_dual";

// removes the garbage can in the flyout and its container, if they exist
function removeDeleteIcon() {
    let container = document.getElementsByClassName(DELETE_BLOCK_ICON_CONTAINER_CLASS_NAME);
    let icon = document.getElementsByClassName(DELETE_BLOCK_ICON_CLASS_NAME);
    for (let element of container) {
        element.remove();
    }
    for (let element of icon) {
        element.remove();
    }
}

// if the mouse has left the region spanning a block and its corresponding garbage can in the flyout,
// remove the garbage can
function checkDeleteIcon(mouseX, mouseY) {
    if (document.getElementsByClassName(DELETE_BLOCK_ICON_CONTAINER_CLASS_NAME).length > 0) {
        let rect = document.getElementsByClassName(DELETE_BLOCK_ICON_CONTAINER_CLASS_NAME)[0]
            .getBoundingClientRect();
        if (mouseX < rect.left || mouseX > rect.right || mouseY < rect.top ||
            mouseY > rect.bottom) {
            removeDeleteIcon();
        }
    }
}

// keep track of mouse position
function logMousePosition(e) {
    checkDeleteIcon(e.clientX, e.clientY);
}

// removes the warning symbol next to a library name, if it exists
function removeWarningSymbol() {
    let libraryTitleWarningIconDiv = document.getElementById(LIBRARY_TITLE_WARNING_ICON_CONTAINER_ID);
    if (libraryTitleWarningIconDiv) libraryTitleWarningIconDiv.remove();
}

function generateSnapReturns(blockId, blockType) {
    // create variable blocks for any SnapReturns variables found in the JavaScript code
    let block = Blockly.mainWorkspace.getBlockById(blockId);
    if (block != null && block.type === blockType) {
        let blockScript = block.code || block.getCode();
        try {
            // generate AST for code
            let ast = Parser.parse(blockScript);

            // find the expressions in the AST containing SnapReturns and keep track of all the variable names used
            let vars = [];
            simple(ast, {
                ExpressionStatement(node) {
                    if (node.expression.left?.object?.name === "SnapReturns" &&
                        node.expression.left?.property?.name) {
                        vars.push(node.expression.left.property.name);
                    }
                }
            });

            // create variable blocks for each of the SnapReturns variables
            for (let variable of vars) {
                Blockly.mainWorkspace.createVariable(variable);
            }
        } catch (parseError) {
            console.warn('Failed to parse block code for SnapReturns:', parseError);
        }
    }
}

// expands the toolbox rows when the toolbox is not collapsed
function expandToolboxRows() {
    const toolboxRows = document.querySelectorAll('.blocklyTreeRow');
    [...toolboxRows].forEach(row => {
        row.classList.add("blocklyTreeRowExpanded");
    });
}

// repositions the flyout and its scrollbars to line up with the toolbox, returning the toolbox width
function repositionFlyout(zoomLevel) {
    const toolboxDiv = document.querySelector('.blocklyToolboxDiv');
    const flyout = document.getElementById("flyout");
    const flyoutScrollbars = document.querySelectorAll('.blocklyFlyoutScrollbar');
    const toolboxWidth = toolboxDiv.offsetWidth;
    flyout.style.transform = "translate(" + toolboxWidth + "px, 0px)";
    const flyoutBoundingRectRight = flyout.getBoundingClientRect().right;
    [...flyoutScrollbars].forEach(scrollbar => {
        scrollbar.style.transform =
            `translate(${flyoutBoundingRectRight - SCROLLBAR_HORIZONTAL_OFFSET}px, ${SCROLLBAR_VERTICAL_OFFSET}px)`;
    })
    return toolboxWidth;
}

// shows the tooltip explaining the library title warning icon
function showLibraryWarningTooltip(iconSpanDiv, zoomLevel, workspaceMountDiv) {
    if (document.getElementById(LIBRARY_TITLE_WARNING_TOOLTIP_ID)) return;
    const warningIconBoundingRect = iconSpanDiv.getBoundingClientRect();

    const tooltipDiv = document.createElement("div");
    tooltipDiv.id = LIBRARY_TITLE_WARNING_TOOLTIP_ID;
    tooltipDiv.className = "blocklyTooltipDiv";
    tooltipDiv.textContent = LIBRARY_TITLE_WARNING_TOOLTIP;

    tooltipDiv.style.left = (warningIconBoundingRect.right +
        DEFAULT_WARNING_SYMBOL_SPACING * zoomLevel) + "px";
    tooltipDiv.style.top = warningIconBoundingRect.top + "px";
    tooltipDiv.style.maxWidth = (DEFAULT_WARNING_SYMBOL_TOOLTIP_WIDTH * zoomLevel) + "px";
    tooltipDiv.style.fontSize = (DEFAULT_WARNING_SYMBOL_TOOLTIP_FONT_SIZE * zoomLevel) + "px";

    workspaceMountDiv.appendChild(tooltipDiv);
}

// hides the tooltip explaining the library title warning icon
function hideLibraryWarningTooltip() {
    const tooltipDiv = document.getElementById(LIBRARY_TITLE_WARNING_TOOLTIP_ID);
    if (tooltipDiv) tooltipDiv.remove();
}

// adds a warning symbol next to the name of the library and a tooltip for the warning symbol, if needed
function addLibraryTitleWarningIcon(flyoutDOM, zoomLevel, workspaceMountDiv) {
    let libraryTitleWithWarning = flyoutDOM.getElementsByClassName("has-warning");
    if (libraryTitleWithWarning.length === 0) return;
    libraryTitleWithWarning = libraryTitleWithWarning[0];
    if (!libraryTitleWithWarning || document.getElementById(LIBRARY_TITLE_WARNING_ICON_CONTAINER_ID)) return;

    const libraryTitleBoundingRect = libraryTitleWithWarning.getBoundingClientRect();

    const iconSpanDiv = document.createElement("div");
    iconSpanDiv.id = LIBRARY_TITLE_WARNING_ICON_CONTAINER_ID;

    iconSpanDiv.style.left = (libraryTitleBoundingRect.right +
        DEFAULT_WARNING_SYMBOL_SPACING * zoomLevel) + "px";
    iconSpanDiv.style.top = (((libraryTitleBoundingRect.top + libraryTitleBoundingRect.bottom) / 2) -
        (DEFAULT_WARNING_SYMBOL_FONT_SIZE * zoomLevel / 2)) + "px";
    iconSpanDiv.style.height = (DEFAULT_WARNING_SYMBOL_FONT_SIZE * zoomLevel) + "px";
    iconSpanDiv.style.width = (DEFAULT_WARNING_SYMBOL_FONT_SIZE * zoomLevel) + "px";

    const iconSpan = document.createElement("span");
    iconSpan.id = LIBRARY_TITLE_WARNING_ICON_ID;
    iconSpan.className = `icon with-icon-paths ${WARNING_ICON_CLASS}`;
    iconSpan.style.fontSize = (DEFAULT_WARNING_SYMBOL_FONT_SIZE * zoomLevel) + "px";
    iconSpanDiv.appendChild(iconSpan);

    workspaceMountDiv.appendChild(iconSpanDiv);
    Blockly.utils.ensureIconPathsExist();

    // add tooltip for warning symbol
    iconSpanDiv.addEventListener("mouseover", () => showLibraryWarningTooltip(iconSpanDiv, zoomLevel, workspaceMountDiv));
    iconSpanDiv.addEventListener("mouseout", hideLibraryWarningTooltip);
}

class Workspace extends Component {
    componentDidUpdate(prevProps) {
        if (prevProps.myBlocks !== this.props.myBlocks) {
            let selected = document.querySelector(".blocklySelected");
            if (selected) {
                selected.style.opacity = "1";
                this.props.workspace.scrollCenter();
            }
        }
    }

    componentDidMount() {
        this.setState({
            userId: -1,
            tenantId: -1,
            snapTenantGuid: null,
            libraryId: -1,
            blockId: -1,
            libraryName: "",
            blockName: ""
        });

        // Workspaces injection
        // var workspace = Blockly.inject('blocklyDiv', {toolbox: document.getElementById('toolbox')});
        setTimeout(() => {
            // keep track of mouse position
            document.addEventListener('mousemove', logMousePosition);

            Blockly.mainWorkspace.addChangeListener(blocklyEvent => this.handleBlocklyEvent(blocklyEvent));
        }, 1000);
    }

    // dispatches a Blockly workspace change event to the appropriate handler
    handleBlocklyEvent(blocklyEvent) {
        switch (blocklyEvent.type) {
            case Blockly.Events.UI:
                this.handleBlocklyUIEvent(blocklyEvent);
                break;
            case Blockly.Events.CREATE:
                generateSnapReturns(blocklyEvent.blockId, "custom_js_block");
                generateSnapReturns(blocklyEvent.blockId, "blank_block");
                break;
            case "JsEditorClose":
                generateSnapReturns(blocklyEvent.blockId, "snap_jsblocks_custom_js");
                break;
            default:
                break;
        }
    }

    // handles Blockly UI events (toolbox/flyout expansion, category selection, warning symbols)
    handleBlocklyUIEvent(blocklyEvent) {
        const zoomLevel = Blockly.getMainWorkspace().scale;

        if (document.querySelector('.blocklyToolboxDiv').width > TOOLBOX_COLLAPSED_WIDTH) {
            expandToolboxRows();
        }

        if (blocklyEvent.element === "category") {
            const toolboxWidth = repositionFlyout(zoomLevel);
            if (toolboxWidth > TOOLBOX_COLLAPSED_WIDTH) {
                expandToolboxRows();
            }
        }

        if (blocklyEvent.element === "category" || blocklyEvent.element === "selected") {
            removeWarningSymbol();
        }

        // get names of libraries
        const libraries = this.props.libraries.map(library => library.name);

        if (blocklyEvent.element === "category" && libraries.includes(blocklyEvent.newValue)) {
            this.handleLibraryCategorySelected(blocklyEvent, zoomLevel);
        }
    }

    // handles selection of a custom library category in the toolbox, adding garbage can icons
    // to its blocks and a warning symbol next to the library title if needed
    handleLibraryCategorySelected(blocklyEvent, zoomLevel) {
        const workspaceMountDiv = document.getElementById("workspaceMountDiv");

        // add garbage can icon to blocks in custom libraries
        let flyoutDOM = document.getElementsByClassName("blocklyFlyout");
        if (flyoutDOM && flyoutDOM.length > 1) {
            // get DOM element for toolbox flyout (flyoutDOM[0] represents the trash can flyout)
            flyoutDOM = flyoutDOM[1];
            this.addGarbageCanIconsToFlyout(flyoutDOM, zoomLevel, blocklyEvent, workspaceMountDiv);
        }

        addLibraryTitleWarningIcon(flyoutDOM, zoomLevel, workspaceMountDiv);
    }

    // increases the flyout width to fit garbage can icons and attaches hover/click listeners
    // to each top-level block in the flyout
    addGarbageCanIconsToFlyout(flyoutDOM, zoomLevel, blocklyEvent, workspaceMountDiv) {
        // increase width of flyout to accommodate garbage can
        let flyoutWidth = flyoutDOM.getAttribute("width");
        flyoutWidth = Number(flyoutWidth.match(/\d+/)[0]);
        flyoutWidth = (flyoutWidth + DEFAULT_FLYOUT_WIDTH_INCREASE * zoomLevel) + "px";
        flyoutDOM.setAttribute("width", flyoutWidth);

        // move scrollbar to the right edge of the flyout
        let flyoutScrollbar = document.getElementsByClassName("blocklyFlyoutScrollbar");
        flyoutScrollbar = Array.prototype.slice.call(flyoutScrollbar);
        if (flyoutScrollbar.length > 1) {
            flyoutScrollbar = flyoutScrollbar[1];
            let flyoutScrollbarTransform = flyoutScrollbar.style.transform;
            let regex = /^\D*(\d+(?:\.\d+)?)/;
            if (flyoutScrollbarTransform !== "" && flyoutScrollbarTransform.match(regex)?.length > 1) {
                let flyoutScrollbarTransformX = flyoutScrollbarTransform.match(regex)[1];
                let newFlyoutScrollbarTransformX = (Number(flyoutScrollbarTransformX) +
                    DEFAULT_FLYOUT_WIDTH_INCREASE * zoomLevel).toString();
                flyoutScrollbar.style.transform = flyoutScrollbarTransform.replace(
                    flyoutScrollbarTransformX, newFlyoutScrollbarTransformX
                );
            }
        }

        // get top blocks inside flyout
        let draggables = flyoutDOM.getElementsByClassName("blocklyDraggable");
        let draggablesArray = Array.prototype.slice.call(draggables);
        let i = 0;
        while (i < draggablesArray.length) {
            // blocks nested inside custom blocks also appear as draggables in the DOM
            // remove these from the list as we only want the top blocks
            if (draggablesArray[i].parentNode.classList.contains("blocklyDraggable")) {
                draggablesArray.splice(i, 1);
            } else {
                i++;
            }
        }

        // create garbage can icon for a block when the mouse hovers over that block
        for (let index = 0; index < draggablesArray.length; index++) {
            this.attachGarbageCanHoverListener(draggablesArray, index, zoomLevel, blocklyEvent, workspaceMountDiv);

            // if the mouse is pressed down over a block in the flyout, remove the garbage can icon
            draggablesArray[index].addEventListener("pointerdown", removeDeleteIcon);
        }
    }

    // shows a garbage can icon next to a flyout block while the mouse hovers over it
    attachGarbageCanHoverListener(draggablesArray, index, zoomLevel, blocklyEvent, workspaceMountDiv) {
        draggablesArray[index].addEventListener("mouseover", e => {
            if (document.getElementsByClassName(DELETE_BLOCK_ICON_CONTAINER_CLASS_NAME).length === 0 &&
                document.getElementsByClassName(DELETE_BLOCK_ICON_CLASS_NAME).length === 0 &&
                e.buttons === 0) {
                let blockRect = draggablesArray[index].getBoundingClientRect();

                // create container that spans the block and the garbage can
                // (if the mouse is over this container, the garbage can should be visible)
                let deleteIconContainer = document.createElement("div");
                deleteIconContainer.classList.add(DELETE_BLOCK_ICON_CONTAINER_CLASS_NAME);
                deleteIconContainer.style.left = blockRect.left + "px";
                deleteIconContainer.style.top = blockRect.top + "px";
                deleteIconContainer.style.width = (blockRect.right - blockRect.left +
                    2 * GARBAGE_CAN_ICON_SPACING + DEFAULT_BLOCK_GARBAGE_CAN_FONT_SIZE * zoomLevel) + "px";
                deleteIconContainer.style.height = (blockRect.bottom - blockRect.top) + "px";

                // create garbage can icon
                let deleteIcon = document.createElement("div");
                deleteIcon.classList.add(DELETE_BLOCK_ICON_CLASS_NAME);
                deleteIcon.style.left = (blockRect.right + GARBAGE_CAN_ICON_SPACING) + "px";
                deleteIcon.style.top = (blockRect.top + DEFAULT_GARBAGE_CAN_ICON_TOP_OFFSET * zoomLevel) + "px";
                deleteIcon.style.width = (DEFAULT_BLOCK_GARBAGE_CAN_FONT_SIZE * zoomLevel) + "px";
                deleteIcon.style.height = (DEFAULT_BLOCK_GARBAGE_CAN_FONT_SIZE * zoomLevel) + "px";

                // if the garbage can icon is clicked, show a dialog to confirm if the user wants
                // to delete the block
                deleteIcon.addEventListener("pointerup", () => {
                    removeDeleteIcon();
                    this.handleDeleteBlockIconClick(blocklyEvent, index);
                })

                // insert container and icon into DOM
                workspaceMountDiv.appendChild(deleteIconContainer);
                workspaceMountDiv.appendChild(deleteIcon);
            }
        });
    }

    // looks up the block targeted by the garbage can icon and navigates to the delete-block dialog
    handleDeleteBlockIconClick(blocklyEvent, index) {
        // get user ID, library ID, block ID, and block name from the database
        let userId = -1;
        let tenantId = -1;
        let snapTenantGuid = null;
        let libraryId = -1;
        let blockId = -1;
        let blockName = "";
        for (const library of this.props.libraries) {
            if (library.name === blocklyEvent.newValue) {
                userId = library.userId;
                tenantId = library.tenantId;
                snapTenantGuid = library.snapTenantGuid;
                libraryId = library.id;
                blockId = library.blocks[index].id;
                blockName = library.blocks[index].name;
            }
        }

        this.setState({
            userId: userId,
            tenantId: tenantId,
            snapTenantGuid: snapTenantGuid,
            libraryId: libraryId,
            blockId: blockId,
            libraryName: blocklyEvent.newValue,
            blockName: blockName
        });

        this.props.history.push(`/delete-block${this.props.location.search}`);
    }

    getDeepComponent (pathname, deleteBlock) {
        if (pathname === '/delete-block') {
            return <DeleteBlockDialog userId={this.state.userId}
                                      tenantId={this.state.tenantId}
                                      snapTenantGuid={this.state.snapTenantGuid}
                                      libraryId={this.state.libraryId}
                                      blockId={this.state.blockId}
                                      libraryName={this.state.libraryName}
                                      blockName={this.state.blockName}
                                      history={this.props.history}
                                      deleteBlock={deleteBlock} />
        }
    }

    render() {
        const {actions} = queryString.parse(globalThis.location.search);
        const {toolboxRef, mountRef, myBlocks, jsBlocks, libraries, deleteBlock, workspace, sendCodeToParent, evalCode, logCode, debug, stepExecution, continueExecution, debugCode, onSearch, app: {searchQuery}, setRestBlockError, modalOpen, modalContent, modalChecked, playServerSound} = this.props;

        return (
            <div className={classNames("workspace", {actions: actions === "true"})}>
                { this.getDeepComponent(globalThis.location.pathname, deleteBlock) }
                <KeyBindings/>
                <AppTour/>
                <ErrorMessage show={this.props.err.show} errMsg={this.props.err.msg} buttons={this.props.err.buttons}/>
                {debug ?
                    <svg style={{display: 'none'}}>
                        <xml id="toolbox" ref={this.props.toolboxRef}></xml>
                    </svg>
                    : <Toolbox toolboxRef={toolboxRef} myBlocks={myBlocks} jsBlocks={jsBlocks} libraries = {libraries} workspace={workspace}
                               context={this.props.app.context} onSearch={onSearch} searchQuery={searchQuery} setRestBlockError={setRestBlockError}
                               openJSEditor={this.props.openJSEditor} setOpen={this.props.setOpen} modalOpen={modalOpen} playServerSound={playServerSound}/>
                }
                <div id={"workspaceMountDiv"} ref={mountRef}/>
                <svg width="14" height="14" version="1.1" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <pattern id="backgroundimagepattern" patternUnits="userSpaceOnUse" width="14" height="14">
                            <image href="/images/workspace/wf-bg.png" x="0" y="0" width="14" height="14" />
                        </pattern>
                    </defs>
                </svg>
                {
                    actions === "true"
                        ? <div className={"actions-container"}>
                            {/*<button className={"save-myblocks-button"} onClick={ saveMyBlock }>Save</button>*/}
                            <button className='btn' onClick={continueExecution}>Continue</button>
                            <button className='btn' onClick={stepExecution}>Step</button>
                            <i>|</i>
                            <button className={"send-code-button btn"} onClick={sendCodeToParent}>Send</button>
                            <button className='btn' onClick={evalCode}>Eval</button>
                            <button className='btn' onClick={debugCode}>Debug</button>
                            <button className='btn' onClick={logCode}>Log Code</button>

                        </div>
                        : null
                }
                <ActionButtons saveMyBlock={this.props.saveMyBlock} modalOpen={modalOpen} modalPopup={this.props.modalPopup} modalContent={modalContent} modalChecked={modalChecked}/>
            </div>
        );
    }
}

Workspace.propTypes = {
    // Redux state slices (see reducers/app.js, reducers/block.js)
    app: PropTypes.object.isRequired,

    // Data passed down from WorkspaceContainer (see containers/Workspace.jsx)
    myBlocks: PropTypes.array,
    jsBlocks: PropTypes.array,
    libraries: PropTypes.array,
    workspace: PropTypes.object,
    toolboxRef: PropTypes.object,
    mountRef: PropTypes.object,
    err: PropTypes.shape({
        show: PropTypes.bool,
        msg: PropTypes.string,
        buttons: PropTypes.array
    }),
    modalOpen: PropTypes.bool,
    modalContent: PropTypes.string,
    modalChecked: PropTypes.bool,
    debug: PropTypes.bool,
    onSearch: PropTypes.func,

    // react-router props
    history: PropTypes.object,
    location: PropTypes.object,

    // Callback props
    deleteBlock: PropTypes.func,
    sendCodeToParent: PropTypes.func,
    evalCode: PropTypes.func,
    logCode: PropTypes.func,
    stepExecution: PropTypes.func,
    continueExecution: PropTypes.func,
    debugCode: PropTypes.func,
    setRestBlockError: PropTypes.func,
    openJSEditor: PropTypes.func,
    setOpen: PropTypes.func,
    saveMyBlock: PropTypes.func,
    modalPopup: PropTypes.func,
    playServerSound: PropTypes.func
};

export default Workspace;
